<template>
  <BaseModal :open="open" maxWidth="420px" @close="onClose">
    <div ref="confirmRoot"
         class="v2026-confirm"
         role="dialog"
         aria-modal="true"
         :aria-label="title || message"
         @keydown.tab="trapTab">
      <div v-if="title" class="v2026-confirm__title">{{ title }}</div>
      <div v-if="message" class="v2026-confirm__msg">{{ message }}</div>

      <div class="v2026-confirm__foot">
        <button ref="cancelBtn" type="button" class="v2026-confirm__btn-ghost" @click="onClose">
          {{ cancelLabel }}
        </button>
        <button ref="confirmBtn"
                type="button"
                class="v2026-confirm__btn"
                :class="'v2026-confirm__btn--' + variant"
                :disabled="busy"
                @click="$emit('confirm')">
          {{ busy ? 'กำลังโหลด...' : confirmLabel }}
        </button>
      </div>
    </div>
  </BaseModal>
</template>

<script>
import BaseModal from './BaseModal'

export default {
  name: 'BaseConfirm',
  components: {
    BaseModal
  },
  props: {
    // version2026: the parent owns the state; there is no v-model.
    open: {
      type: Boolean,
      default: false
    },
    // version2026: the prominent line, 15/600. A title-only confirm is the common shape.
    title: {
      type: String,
      default: ''
    },
    // version2026: optional detail line; the row is hidden when empty.
    message: {
      type: String,
      default: ''
    },
    confirmLabel: {
      type: String,
      default: 'ยืนยัน'
    },
    cancelLabel: {
      type: String,
      default: 'ยกเลิก'
    },
    // version2026: 'primary' | 'danger' — styles the confirm button only.
    variant: {
      type: String,
      default: 'primary'
    },
    // version2026: request in flight — confirm disabled, label swapped, dismissal blocked.
    busy: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    open (isOpen) {
      // A destructive action is never the default focus.
      if (!isOpen) return
      this.$nextTick(() => {
        if (this.$refs.cancelBtn) this.$refs.cancelBtn.focus()
      })
    }
  },
  methods: {
    // Backdrop, Escape and the cancel button all land here. A dialog whose request is in flight
    // must not vanish under the user.
    onClose () {
      if (this.busy) return
      this.$emit('cancel')
    },
    // BaseModal does not trap focus. The two footer buttons are the only focusables.
    trapTab (e) {
      const cancel = this.$refs.cancelBtn
      const confirm = this.$refs.confirmBtn
      if (!cancel) return
      if (!confirm || confirm.disabled) {
        e.preventDefault()
        cancel.focus()
        return
      }
      if (e.shiftKey && document.activeElement === cancel) {
        e.preventDefault()
        confirm.focus()
      } else if (!e.shiftKey && document.activeElement === confirm) {
        e.preventDefault()
        cancel.focus()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
/* Measurements are .v2026-mtc's, verbatim, so MessageTemplateItemV2026's inline confirm can be
   folded in later without a pixel changing. */
.v2026-confirm {
  padding: 24px 20px;
  background: #ffffff;
  border-radius: 24px;
}

.v2026-confirm__title {
  font-size: 15px;
  font-weight: 600;
  color: #212529;
}

.v2026-confirm__msg {
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.5;
  color: #66696C;
}

.v2026-confirm__foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}

.v2026-confirm__btn-ghost {
  height: 36px;
  padding: 0 14px;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  background: #ffffff;
  font-size: 14px;
  color: #212529;
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: var(--v2026-color-hover-bg); }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
}

.v2026-confirm__btn {
  height: 36px;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) { filter: brightness(.94); }
  /* The app-wide teal ring, red variant included — a red focus ring would be a second,
     unprecedented focus colour. */
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
  &:disabled { opacity: .5; cursor: default; }
}

.v2026-confirm__btn--primary { background: var(--v2026-color-main); }
.v2026-confirm__btn--danger { background: #E5484D; }
</style>
